import { useState, type ReactNode } from 'react';
import type { Tasting } from '../domain/cellar';
import { formatMonthShort } from '../domain/months';
import { newestFirst } from '../domain/rating';
import { bottleCount, t, tf } from '../i18n';
import { RatingStars } from './RatingStars';
import './TastingHistoryList.css';

/**
 * A wine's "Historique" (owner, 2026-10-11: formerly "Dégustations", oldest first), the most
 * recent tasting first, same-month tastings latest entered first (T090, spec.md Story 5
 * Acceptance 1, 5): MM/YY, the rating with its stars, the notes, and "hors cave" for a tasting
 * that did not take a bottle from the stock. An unrated tasting reads "Pas de note" (owner,
 * 2026-10-15: rated, if wanted, with its "Modifier").
 * With `renderEdit`, each tasting has a "Modifier" button opening its editable fields and its
 * deletion (User Story 7, T118), kept closed so a long history stays short on a phone.
 */
export function TastingHistoryList({
  tastings,
  renderEdit,
}: {
  tastings: readonly Tasting[];
  /** The tasting's editor; `close` closes it (after a save or a cancel). */
  renderEdit?: (tasting: Tasting, close: () => void) => ReactNode;
}) {
  const [editing, setEditing] = useState<string | undefined>();
  if (tastings.length === 0) return <p>{t('rating.none')}</p>;

  return (
    <ol className="tasting-history">
      {newestFirst(tastings).map((tasting) => (
        <li key={tasting.id} className="tasting-history__item">
          <div className="tasting-history__head">
            <span className="tasting-history__date">{formatMonthShort(tasting.tastedOn)}</span>
            {tasting.rating !== undefined ? (
              <span className="tasting-history__rating">
                <RatingStars rating={tasting.rating} />
                {tf('rating.outOfFive', { rating: String(tasting.rating) })}
              </span>
            ) : (
              <span className="tasting-history__unrated">{t('rating.none')}</span>
            )}
            {tasting.consumedFromStock && (tasting.bottles ?? 1) > 1 && (
              <span className="tasting-history__bottles">{bottleCount(tasting.bottles ?? 1)}</span>
            )}
            {!tasting.consumedFromStock && (
              <span className="tasting-history__outside">{t('tasting.outsideCellar')}</span>
            )}
            {renderEdit && (
              <button
                type="button"
                className="tasting-history__edit"
                aria-expanded={editing === tasting.id}
                onClick={() => setEditing(editing === tasting.id ? undefined : tasting.id)}
              >
                {editing === tasting.id ? t('edit.close') : t('edit.open')}
              </button>
            )}
          </div>
          {tasting.notes && <p className="tasting-history__notes">{tasting.notes}</p>}
          {renderEdit && editing === tasting.id && (
            <div className="tasting-history__editor">
              {renderEdit(tasting, () => setEditing(undefined))}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
